import { LoaderButton } from '@/common/components/loader-button';
import EstimatedPricing from './proceed-to-schedule/estimated-pricing';
import { findPlanIdByTier } from '@/catalog/hooks/use-pdp-insurance-plan-submit';
import { findPlanIdByMileage } from '@/catalog/hooks/use-pdp-select-plan-submit';
import usePackageSummary from '@/catalog/hooks/use-package-summary';
import { InsurancePlanTier, VehiclePlan, InsurancePlan } from '@/catalog/types';
import { Form } from 'react-router';
import { Button } from '@/shadcn-ui/button';
import { motion } from 'motion/react';
import { cn } from '@/shadcn-ui/utils';
import { UseFloatyBarResponse } from '@/catalog/hooks/use-floaty-bar';
import { useCallback } from 'react';
import { scrollToRef } from '@/common/utils/scroll-to-ref';
import * as gtag from '@/common/utils/gtags.client';
import { SubscriptionCFA } from '@/account/types/subscription';
import { getSwapEligibility } from '@/common/utils/swap-state';

interface FloatyBarNavButtonProps {
  text: string;
  sectionInView?: boolean;
  ref: HTMLDivElement | null;
}

function FloatyBarNavButton({ text, sectionInView, ref }: FloatyBarNavButtonProps) {
  const handleClick = useCallback(() => {
    scrollToRef(
      {
        current: ref,
      },
      'top',
      /**
       * Logic behind this offset:
       * - 40px top on floaty bar
       * - 80px height of floaty bar
       *
       * So that leaves approximately 80px (200px - 120px) between floaty bar and ref we scroll to.
       */
      200
    );
  }, [ref]);

  return (
    <Button
      variant="clear"
      className="body-tight h-fit cursor-pointer text-[18px] text-flexcar-neutral0"
      onClick={handleClick}
    >
      <div className="relative inline-block">
        {/* Always-bold version to set width, but invisible */}
        <span className="invisible whitespace-nowrap font-semibold">{text}</span>

        {/* Actual visible text that sits in the same spot */}
        <span
          className={cn(
            'absolute left-0 top-0 whitespace-nowrap transition-all hover:underline',
            {
              'left-[-2px] font-semibold underline': sectionInView,
            }
          )}
        >
          {text}
        </span>
      </div>
    </Button>
  );
}

interface FloatyBarProps
  extends Pick<UseFloatyBarResponse, 'currentSection' | 'sectionRefs'> {
  /** Plan mileage in session cart */
  selectedPlanMileage?: number;
  /** Insurance tier in session cart */
  selectedInsuranceTier?: InsurancePlanTier;
  vehiclePlans: VehiclePlan[];
  insurancePlans?: InsurancePlan[];
  savingsAvailable?: boolean;
  estimatedSavings?: number;
  showFloatyBar?: boolean;
  hasFeatures?: boolean;
  hasSavings?: boolean;
  subscription?: SubscriptionCFA;
  /** Mileage plan on current active subscription */
  currentMileagePlan?: number;
  /** Insurance tier on current active subscription */
  currentInsurancePlanTier?: InsurancePlanTier;
  fullstoryContinueEventDelegate?: () => void;
  handleSavingsClick?: () => void;
  GAS_SAVER_BANNER?: boolean;
}

export default function FloatyBar({
  selectedPlanMileage,
  selectedInsuranceTier,
  vehiclePlans,
  insurancePlans,
  savingsAvailable,
  estimatedSavings,
  showFloatyBar,
  hasFeatures,
  hasSavings,
  subscription,
  currentMileagePlan,
  currentInsurancePlanTier,
  currentSection,
  sectionRefs,
  handleSavingsClick,
  fullstoryContinueEventDelegate,
  GAS_SAVER_BANNER,
}: FloatyBarProps) {
  // Top offset is equivalent to full height of floaty bar. If showing gas saver banner, add 40px to offset for gas saver banner.
  const topOffset = GAS_SAVER_BANNER ? 120 : 80;
  const { canViewSwap } = getSwapEligibility(subscription);
  const selectedMileagePlanId = findPlanIdByMileage(vehiclePlans, selectedPlanMileage);
  const selectedInsurancePlanId = findPlanIdByTier(insurancePlans, selectedInsuranceTier);
  const currentMileagePlanId = findPlanIdByMileage(vehiclePlans, currentMileagePlan);
  const currentInsurancePlanId = findPlanIdByTier(
    insurancePlans,
    currentInsurancePlanTier
  );

  const packageSummary = usePackageSummary({
    selectedMileagePlanId: canViewSwap ? currentMileagePlanId : selectedMileagePlanId,
    selectedInsurancePlanId: canViewSwap
      ? currentInsurancePlanId
      : selectedInsurancePlanId,
    vehiclePlans,
    insurancePlans,
    savingsAvailable,
    estimatedSavings,
  });

  const handleSubmitEvent = () => {
    gtag.event('vehicle_details_continue');
    fullstoryContinueEventDelegate?.();
  };

  const barContent = (
    <div className="mx-[40px] flex w-full max-w-[1280px] items-center justify-between gap-[40px] rounded-full bg-flexcar-neutral100/90 py-[12px] pl-[40px] pr-[12px] shadow-shadow25 backdrop-blur-[20px]">
      {/* Hyperlinks */}
      <div className="flex flex-shrink-0 items-center lg:gap-[24px] xl:gap-[40px]">
        {hasFeatures ? (
          <FloatyBarNavButton
            text="Features & Specs"
            sectionInView={currentSection === 'features'}
            ref={sectionRefs.current['features']}
          />
        ) : null}
        {hasSavings ? (
          <FloatyBarNavButton
            text="Cost comparison"
            sectionInView={currentSection === 'savings'}
            ref={sectionRefs.current['savings']}
          />
        ) : null}
        <FloatyBarNavButton
          text="Inspection"
          sectionInView={currentSection === 'inspection'}
          ref={sectionRefs.current['inspection']}
        />
        <FloatyBarNavButton
          text="Why Flexcar"
          sectionInView={currentSection === 'whyFlexcar'}
          ref={sectionRefs.current['whyFlexcar']}
        />
      </div>

      {/* Estimated pricing and continue button */}
      {/**
       * Note: need min-w-0 here to get the Continue button to properly shrink, otherwise it only shrinks partially before starting to overflow out of the bar
       * https://stackoverflow.com/questions/36247140/why-dont-flex-items-shrink-past-content-size
       */}
      <div className="flex min-w-0 flex-shrink items-center gap-[24px]">
        {packageSummary ? (
          <EstimatedPricing
            estimatedMonthlyCostCopy={packageSummary.estimatedMonthlyCostCopy}
            estimatedMonthlySavingsCopy={packageSummary.estimatedSavingsCopy}
            className="flex-shrink-0 lg:mb-0 lg:w-fit lg:flex-col lg:items-end"
            skeletonClassName="lg:flex-col lg:items-end flex-shrink-0"
            compact={true}
            onClickDelegate={handleSavingsClick}
          />
        ) : (
          <p className="body2-tight w-[126px] flex-shrink-0 text-flexcar-neutral50">
            Choose plans to see monthly total
          </p>
        )}
        <Form method="post" className="flex w-[260px] flex-shrink">
          <input
            type="hidden"
            name="plan"
            value={canViewSwap ? currentMileagePlanId : selectedMileagePlanId}
          />
          <input
            type="hidden"
            name="insurance_plan_id"
            value={canViewSwap ? currentInsurancePlanId : selectedInsurancePlanId}
          />
          <LoaderButton
            variant="primary"
            type="submit"
            name="intent"
            value="plan-with-insurance"
            className="flex w-full items-center rounded-full px-[48px] py-[12px] hover:bg-flexcar-neutral20"
            onClick={handleSubmitEvent}
          >
            Continue
          </LoaderButton>
        </Form>
      </div>
    </div>
  );

  return (
    <motion.div
      className="fixed left-0 top-[40px] z-10 hidden w-full items-center justify-center lg:flex"
      initial={{ opacity: 0, y: -topOffset, scale: 0.98 }}
      animate={{
        opacity: showFloatyBar ? 1 : 0,
        y: showFloatyBar ? 0 : -topOffset,
        scale: showFloatyBar ? 1 : 0.98,
      }}
      transition={{
        duration: 0.5,
        ease: 'easeInOut',
      }}
    >
      {barContent}
    </motion.div>
  );
}
